// Replace/update blocks based on current device.
// - replaces video blocks urls with low-res versions (mobile)
// - replaces image blocks with screen/block-appropriate versions from source-sets (mobile)
// - removes blur effects (mobile)

import type CreativeEngine from "@cesdk/engine";
import type {DesignBlockId} from "@cesdk/cesdk-js";
import {
    BLOCK_VIDEO_LOWRES_URI_META_KEY,
} from "../constants.ts";
import {getPageVideoBlocks, getVisualPageBlocks, swapVideoFillUri} from "../utils/slideUtilities.ts";
import {isMobile} from "../utils/browser.ts";
import {useManualRendering} from "./video-renderer.ts";
import type {SlideInfo} from "../api/presentations.ts";
import {decodeBase64ToUtf8, encodeUtf8ToBase64} from "../utils/sceneDataUtilities.ts";

// testing parameters
const TEST_ALWAYS_REPLACE:boolean = false;

const SUPPORTED_VIDEO_FORMATS = ["mp4", "mov", "webm"];
const IGNORE_VIDEO_FORMATS = ["gif"];

let _useLowRes:boolean | undefined;
export function useLowRes(): boolean {
    if (_useLowRes !== undefined)
        return _useLowRes;

    if (isMobile()) {
        _useLowRes = true;
        return _useLowRes;
    }

    if (TEST_ALWAYS_REPLACE) {
        _useLowRes = true;
        return _useLowRes;
    }

    _useLowRes = false;
    return _useLowRes;
}

export function configureSizing(engine: CreativeEngine)
{
    const params = new URLSearchParams(window.location.search);

    // lower image size (memory usage) for non-source set images
    if (isMobile() || params.has('reduce')) {
        engine.editor.setSetting('maxImageSize', 1024);
    }
    else {
        engine.editor.setSetting('maxImageSize', 2048);
    }
}

/**
 * Monitor ce.sdk URI requests for videos.
 * On mobile devices, it rewrites high-quality video URIs to low-quality versions.
 * When manual rendering is in use, it disables videos (by substituting an empty video).
 */
export function uriResolver(uri: string, defaultURIResolver: (URI: string) => string): string
{
    if (!useLowRes() && !useManualRendering())
        return defaultURIResolver(uri);

    function isVideoUri(uri: string) {
        const noQuery = uri.split(/[?#]/, 1)[0];
        return noQuery.includes("/videos/") && noQuery.endsWith(".mp4");
    }

    function rewriteHqToLq(uri: string): string {
        return uri.endsWith("_hq.mp4")
            ? uri.slice(0, -"_hq.mp4".length) + "_lq.mp4"
            : uri;
    }

    if (isVideoUri(uri))
    {
        if (useManualRendering()) {
            // manual rendering - return empty data URI and we'll load the video
            return "https://r2-dev.myidecide.net/blank.mp4";
            //return "data:image/gif;base64,R0lGODlhAQABAAAAACw=\n"; // blank gif
        }
        else {
            // ce.sdk rendering - check if we should force a low-res version of the video
            if (useLowRes())
                return rewriteHqToLq(uri);
            else
                return uri;
        }
    }

    return defaultURIResolver(uri);
}

export async function processSlide(engine: CreativeEngine, pageId: DesignBlockId, replaceVideos:boolean = false)
{
    if (replaceVideos)
        await checkVideoReplacement(engine, pageId);

    if (isMobile())
        await removeBlur(engine, pageId);
}

/**
 * Replace video blocks with low-res versions (if a mobile device and low-res versions are present in metadata).
 * Call when a slide is about to play (before forceLoadResources() is called).
 */
async function checkVideoReplacement(engine: CreativeEngine, pageId: DesignBlockId): Promise<boolean>
{
    if (!useLowRes())
        return false;

    // replace video blocks with low-res versions (if present in metadata)
    const blocks: DesignBlockId[] = getPageVideoBlocks(engine, pageId, IGNORE_VIDEO_FORMATS);

    for (const id of blocks) {
        if (!engine.block.hasMetadata(id, BLOCK_VIDEO_LOWRES_URI_META_KEY)) {
            // no low-res version available
            continue;
        }

        const lowResUri = engine.block.getMetadata(id, BLOCK_VIDEO_LOWRES_URI_META_KEY);
        if (lowResUri) {
            swapVideoFillUri(engine, id, lowResUri);
        }
    }

    return true;
}

async function removeBlur(engine: CreativeEngine, pageId: DesignBlockId)
{
    const blocks: DesignBlockId[] = getVisualPageBlocks(engine, pageId);

    for (const id of blocks) {
        if (engine.block.supportsBlur(id) && engine.block.isBlurEnabled(id))
            engine.block.setBlurEnabled(id, false);
    }
}

export function adjustImageSizing(slides: SlideInfo[])
{
    const params = new URLSearchParams(window.location.search);

    // determine target DPR
    const deviceRatio: number = window.devicePixelRatio || 1;
    let dprImageFactor: number;

    if (isMobile()) {
        if (deviceRatio >= 3)
            dprImageFactor = 1.5;    // common (iPhone) and above; target 1.5 DPR (half the iPhone resolution)
        else
            dprImageFactor = 1;      // lower density, just use 1:1
    }
    else {
        dprImageFactor = deviceRatio;
    }

    if (params.has('dpr'))
        dprImageFactor = parseFloat(params.get('dpr') ?? "1");

    // update each slide - removing unneeded sourcesets (videos and images)
    for (const slide of slides) {
        if (!slide.slideData)
            continue;

        let json: string = decodeBase64ToUtf8(slide.slideData);

        let hadP5Prefix: boolean = false;
        if (json.startsWith("P\x145")) {
            hadP5Prefix = true;
            json = json.slice(3);
        }

        const parsed = JSON.parse(json);
        patchSceneImageSourceSets(parsed, dprImageFactor, isMobile());
        patchSceneVideoSourceSets(parsed, isMobile() ? 1 : deviceRatio, isMobile());
        slide.slideData = encodeUtf8ToBase64((hadP5Prefix ? "P\x145" : "") + JSON.stringify(parsed));
    }
}

function patchSceneImageSourceSets(sceneObj: any, targetDpr: number, useBothDimsOnMobile: boolean = false): any {
    if (!sceneObj || typeof sceneObj !== "object") return sceneObj;

    // --- Helpers ---------------------------------------------------------------

    const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
    const clampDpr = (d: number) => (isFinite(d) && d > 0 ? clamp(d, 0.5, 4) : 1);

    const get = (obj: any, path: string[]): any => {
        let cur = obj;
        for (const k of path) {
            if (!cur || typeof cur !== "object") return undefined;
            cur = cur[k];
        }
        return cur;
    };

    const toNum = (v: any): number | null => (typeof v === "number" && isFinite(v) ? v : null);

    function readEntityId(node: any): number | null {
        const e = toNum(node?.entity);
        return e != null ? e : null;
    }

    function readBlockSizeDesign(node: any): { w: number; h: number } | null {
        const w = toNum(get(node, ["block_size", "width", "value"]));
        const h = toNum(get(node, ["block_size", "height", "value"]));
        if (w && h && w > 0 && h > 0) return { w, h };
        return null;
    }

    function readFillRefEntityId(node: any): number | null {
        const v = toNum(get(node, ["block_fill", "value"]));
        return v != null ? v : null;
    }

    function readCropScale(node: any): { sx: number; sy: number } {
        // UBQ: crop scale stored on the graphic block
        const sx = toNum(node?.block_crop_scale_x) ?? 1;
        const sy = toNum(node?.block_crop_scale_y) ?? 1;
        // Only scale up requirements if zoomed in; zooming out doesn't require more pixels.
        return { sx: Math.max(1, sx), sy: Math.max(1, sy) };
    }

    function readSourceW(entry: any): number | null {
        return toNum(entry?.width) ?? toNum(entry?.w);
    }
    function readSourceH(entry: any): number | null {
        return toNum(entry?.height) ?? toNum(entry?.h);
    }

    function chooseBestSource(sourceSet: any[], reqW: number, reqH: number): any {
        const norm = sourceSet
            .map((e) => ({ e, w: readSourceW(e), h: readSourceH(e) }))
            .filter((x) => x.e && typeof x.w === "number" && (x.w as number) > 0);

        if (!norm.length) return sourceSet[sourceSet.length - 1];

        // sort ascending by width (tie-break by height if present)
        norm.sort((a, b) => {
            if ((a.w as number) !== (b.w as number)) return (a.w as number) - (b.w as number);
            const ah = a.h ?? 0;
            const bh = b.h ?? 0;
            return ah - bh;
        });

        const anyHeights = norm.some((x) => typeof x.h === "number" && x.h! > 0);
        const requireBoth = useBothDimsOnMobile && anyHeights;
        if (requireBoth) {
          // MOBILE: enforce both dimensions
              const meet = norm.find((x) => (x.w as number) >= reqW && (x.h as number) >= reqH);
          if (meet) return meet.e;
        } else {
          // DESKTOP (or if heights missing): width-only
              const meet = norm.find((x) => (x.w as number) >= reqW);
          if (meet) return meet.e;
        }

        return norm[norm.length - 1].e;
    }

    function computeViewportScale(
        pageDesign: { w: number; h: number } | null,
        useBothDimsOnMobile: boolean
    ): number {
        if (!pageDesign) return 1;

        const vw = Math.max(1, window.innerWidth || 1);
        const vh = Math.max(1, window.innerHeight || 1);

        const s1 = Math.min(vw / pageDesign.w, vh / pageDesign.h);

        if (!useBothDimsOnMobile) {
            return clamp(s1, 0.05, 1);
        }

        // mobile: choose the LARGER possible fit-scale so image is safe for either orientation
        const s2 = Math.min(vh / pageDesign.w, vw / pageDesign.h);
        return clamp(Math.max(s1, s2), 0.05, 1);
    }

    // --- Pass 0: find page size (design units) --------------------------------
    // Used to approximate how much the page is scaled down to fit the viewport.
    let pageDesign: { w: number; h: number } | null = null;

    {
        const stack: any[] = [sceneObj];
        while (stack.length) {
            const node = stack.pop();
            if (!node || typeof node !== "object") continue;

            if (node?.id === "//ly.img.ubq/page") {
                const sz = readBlockSizeDesign(node);
                if (sz) {
                    // If multiple pages exist, keep the largest (more conservative).
                    if (!pageDesign) pageDesign = sz;
                    else pageDesign = { w: Math.max(pageDesign.w, sz.w), h: Math.max(pageDesign.h, sz.h) };
                }
            }

            for (const k of Object.keys(node)) stack.push(node[k]);
        }
    }

    const dpr = clampDpr(targetDpr);
    const viewportScale = computeViewportScale(pageDesign, useBothDimsOnMobile);

    // map fill entity -> required DESIGN footprint
    // We store requirements in design-units, already inflated for crop zoom,
    // then later convert to required pixels via viewportScale * dpr.
    const reqDesignByFill = new Map<number, { w: number; h: number }>();

    {
        const stack: any[] = [sceneObj];
        while (stack.length) {
            const node = stack.pop();
            if (!node || typeof node !== "object") continue;

            const blockDesign = readBlockSizeDesign(node);
            if (blockDesign) {
                const fillId = readFillRefEntityId(node);
                if (fillId != null) {
                    // Crop/zoom increases the needed pixels for the same on-screen size
                    const { sx, sy } = readCropScale(node);
                    const needWDesign = blockDesign.w * sx;
                    const needHDesign = blockDesign.h * sy;

                    const prev = reqDesignByFill.get(fillId);
                    if (!prev) {
                        reqDesignByFill.set(fillId, { w: needWDesign, h: needHDesign });
                    } else {
                        reqDesignByFill.set(fillId, { w: Math.max(prev.w, needWDesign), h: Math.max(prev.h, needHDesign) });
                    }
                }
            }

            for (const k of Object.keys(node)) stack.push(node[k]);
        }
    }

    // shrink fill/image sourceSets to 1 entry
    {
        const stack: any[] = [sceneObj];
        while (stack.length) {
            const node = stack.pop();
            if (!node || typeof node !== "object") continue;

            // only touch UBQ fill/image nodes
            if (node?.id === "//ly.img.ubq/fill/image") {
                const ss = node?.sourceSet;
                if (Array.isArray(ss) && ss.length > 1) {
                    const fillEntityId = readEntityId(node);
                    const reqDesign = fillEntityId != null ? reqDesignByFill.get(fillEntityId) : null;

                    // Convert design requirement → rendered CSS px → required source pixels
                    // rendered size shrinks with viewportScale; required pixels scales with target DPR.
                    const reqW = reqDesign
                        ? Math.max(1, Math.round(reqDesign.w * viewportScale * dpr))
                        : 1200; // fallback if we can't link fill->consumer
                    const reqH = reqDesign
                        ? Math.max(1, Math.round(reqDesign.h * viewportScale * dpr))
                        : 675;

                    const chosen = chooseBestSource(ss, reqW, reqH);
                    node.sourceSet = [chosen];

                    // Keep single-uri fields consistent so nothing else grabs an old URI
                    const chosenUri = chosen?.uri;
                    if (typeof chosenUri === "string" && chosenUri.length) {
                        if ("imageFileURI" in node) node.imageFileURI = chosenUri;
                        if ("previewFileURI" in node) node.previewFileURI = chosenUri;
                    }
                }
            }

            for (const k of Object.keys(node)) stack.push(node[k]);
        }
    }

    return sceneObj;
}

function patchSceneVideoSourceSets(sceneObj: any, targetDpr: number, useBothDimsOnMobile: boolean = false): any {
    if (!sceneObj || typeof sceneObj !== "object") return sceneObj;

    const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
    const clampDpr = (d: number) => (isFinite(d) && d > 0 ? clamp(d, 0.5, 4) : 1);

    const get = (obj: any, path: string[]): any => {
        let cur = obj;
        for (const k of path) {
            if (!cur || typeof cur !== "object") return undefined;
            cur = cur[k];
        }
        return cur;
    };

    const toNum = (v: any): number | null => (typeof v === "number" && isFinite(v) ? v : null);

    function readEntityId(node: any): number | null {
        const e = toNum(node?.entity);
        return e != null ? e : null;
    }

    function readBlockSizeDesign(node: any): { w: number; h: number } | null {
        const w = toNum(get(node, ["block_size", "width", "value"]));
        const h = toNum(get(node, ["block_size", "height", "value"]));
        if (w && h && w > 0 && h > 0) return { w, h };
        return null;
    }

    function readFillRefEntityId(node: any): number | null {
        const v = toNum(get(node, ["block_fill", "value"]));
        return v != null ? v : null;
    }

    function readCropScale(node: any): { sx: number; sy: number } {
        const sx = toNum(node?.block_crop_scale_x) ?? 1;
        const sy = toNum(node?.block_crop_scale_y) ?? 1;
        return { sx: Math.max(1, sx), sy: Math.max(1, sy) };
    }

    function readSourceW(entry: any): number | null {
        return toNum(entry?.width) ?? toNum(entry?.w);
    }

    function readSourceH(entry: any): number | null {
        return toNum(entry?.height) ?? toNum(entry?.h);
    }

    function computeViewportScale(
        pageDesign: { w: number; h: number } | null,
        useBothDimsOnMobile: boolean
    ): number {
        if (!pageDesign) return 1;

        const vw = Math.max(1, window.innerWidth || 1);
        const vh = Math.max(1, window.innerHeight || 1);

        const s1 = Math.min(vw / pageDesign.w, vh / pageDesign.h);

        if (!useBothDimsOnMobile) {
            return clamp(s1, 0.05, 1);
        }

        const s2 = Math.min(vh / pageDesign.w, vw / pageDesign.h);
        return clamp(Math.max(s1, s2), 0.05, 1);
    }

    function chooseBestSource(sourceSet: any[], reqW: number, reqH: number): any {
        const norm = sourceSet
            .map((e) => ({ e, w: readSourceW(e), h: readSourceH(e) }))
            .filter((x) => x.e && typeof x.w === "number" && (x.w as number) > 0);

        if (!norm.length) return sourceSet[sourceSet.length - 1];

        norm.sort((a, b) => {
            if ((a.w as number) !== (b.w as number)) return (a.w as number) - (b.w as number);
            const ah = a.h ?? 0;
            const bh = b.h ?? 0;
            return ah - bh;
        });

        const anyHeights = norm.some((x) => typeof x.h === "number" && x.h! > 0);
        const requireBoth = useBothDimsOnMobile && anyHeights;
        let chosen = requireBoth
            ? norm.find((x) => (x.w as number) >= reqW && (x.h as number) >= reqH)?.e
            : norm.find((x) => (x.w as number) >= reqW)?.e;

        if (!chosen) {
            chosen = norm[norm.length - 1].e;
        }

        if (useBothDimsOnMobile && norm.length > 1 && chosen === norm[norm.length - 1].e) {
            chosen = norm[norm.length - 2].e;
        }

        return chosen;
    }

    let pageDesign: { w: number; h: number } | null = null;

    {
        const stack: any[] = [sceneObj];
        while (stack.length) {
            const node = stack.pop();
            if (!node || typeof node !== "object") continue;

            if (node?.id === "//ly.img.ubq/page") {
                const sz = readBlockSizeDesign(node);
                if (sz) {
                    if (!pageDesign) pageDesign = sz;
                    else pageDesign = { w: Math.max(pageDesign.w, sz.w), h: Math.max(pageDesign.h, sz.h) };
                }
            }

            for (const k of Object.keys(node)) stack.push(node[k]);
        }
    }

    const dpr = clampDpr(targetDpr);
    const viewportScale = computeViewportScale(pageDesign, useBothDimsOnMobile);

    const reqDesignByFill = new Map<number, { w: number; h: number }>();

    {
        const stack: any[] = [sceneObj];
        while (stack.length) {
            const node = stack.pop();
            if (!node || typeof node !== "object") continue;

            const blockDesign = readBlockSizeDesign(node);
            if (blockDesign) {
                const fillId = readFillRefEntityId(node);
                if (fillId != null) {
                    const { sx, sy } = readCropScale(node);
                    const needWDesign = blockDesign.w * sx;
                    const needHDesign = blockDesign.h * sy;

                    const prev = reqDesignByFill.get(fillId);
                    if (!prev) {
                        reqDesignByFill.set(fillId, { w: needWDesign, h: needHDesign });
                    } else {
                        reqDesignByFill.set(fillId, { w: Math.max(prev.w, needWDesign), h: Math.max(prev.h, needHDesign) });
                    }
                }
            }

            for (const k of Object.keys(node)) stack.push(node[k]);
        }
    }

    {
        const stack: any[] = [sceneObj];
        while (stack.length) {
            const node = stack.pop();
            if (!node || typeof node !== "object") continue;

            if (node?.id === "//ly.img.ubq/fill/video") {
                const ss = node?.sourceSet;
                if (Array.isArray(ss) && ss.length > 1) {
                    const fillEntityId = readEntityId(node);
                    const reqDesign = fillEntityId != null ? reqDesignByFill.get(fillEntityId) : null;

                    const reqW = reqDesign
                        ? Math.max(1, Math.round(reqDesign.w * viewportScale * dpr))
                        : 1200;
                    const reqH = reqDesign
                        ? Math.max(1, Math.round(reqDesign.h * viewportScale * dpr))
                        : 675;

                    const chosen = chooseBestSource(ss, reqW, reqH);

                    const lines = ss.map((entry: any) => {
                        const selected = entry === chosen ? "*" : " ";
                        const url = typeof entry?.uri === "string" ? entry.uri : (typeof entry?.url === "string" ? entry.url : "");
                        const width = readSourceW(entry);
                        const height = readSourceH(entry);
                        return `${selected} url=${url || "(none)"}, width=${width ?? "(none)"}, height=${height ?? "(none)"}`;
                    });
                    console.log("[patchSceneVideoSourceSets] requested: " + reqW + "x" + reqH);
                    console.log("[patchSceneVideoSourceSets]\n" + lines.join("\n"));

                    node.sourceSet = [chosen];

                    const chosenUri = typeof chosen?.uri === "string"
                        ? chosen.uri
                        : (typeof chosen?.url === "string" ? chosen.url : undefined);
                    if (typeof chosenUri === "string" && chosenUri.length) {
                        if ("videoFileURI" in node) node.videoFileURI = chosenUri;
                        if ("previewFileURI" in node) node.previewFileURI = chosenUri;
                    }
                }
            }

            for (const k of Object.keys(node)) stack.push(node[k]);
        }
    }

    return sceneObj;
}

